import './PlaintextEditor.scss';

import classNames from 'classnames';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import { EditBox } from '@ui/EditBox';
import { haltClickEventPropagation } from '@lib/events';
import { OutsideClickWrapper } from '@ui/general';
import { TextAreaRef } from 'antd/es/input/TextArea';

import { Button, Input, Tooltip } from 'antd';
import { ProcessingResult, useCurrentUser } from '@hooks';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SonarInputRule, validateText } from '@lib/validation';

export interface PlaintextEditorRef {
	edit: () => void;
	save: () => void;
}

export type PlaintextEditorState = {
	editingValue: string,
	isEditing: boolean,
	isProcessing: boolean,
	showValidation: boolean,
	validationMessage: string
};

export interface PlaintextEditorProps {
	allowNewline?: boolean;
	canBeEmpty?: boolean;
	className?: string;
	hideButtons?: boolean;
	id?: string;
	isDisabled?: boolean;
	isPeriscope?: boolean;
	isProcessing?: boolean;
	isReadOnly?: boolean;
	isSalesforce?: boolean;
	isSpreadsheet?: boolean;
	maxLength?: number;
	maxRows?: number;
	onBlur?: () => void;
	onCancel?: () => void;
	onChange?: (value: string) => void;
	onFocus?: () => void;
	onSave: (value: string) => void | Promise<void>;
	onStartEditing?: () => void;
	placeholder?: string;
	processingResult?: ProcessingResult;
	rules?: Array<SonarInputRule>;
	saveOnBlur?: boolean;
	saveOnEnter?: boolean;
	saveOnTab?: boolean;
	showProcessing?: boolean;
	skipSpellcheck?: boolean;
	tooltip?: string | null;
	value: string | null | undefined;
}

function hasNewline(value: string): boolean {
	return (/\r|\n/u).test(value);
}

export const PlaintextEditor = forwardRef<PlaintextEditorRef | null, PlaintextEditorProps>(({
	allowNewline,
	canBeEmpty,
	className,
	hideButtons,
	id,
	isDisabled,
	isPeriscope,
	isProcessing,
	isReadOnly,
	isSalesforce,
	isSpreadsheet,
	maxLength,
	maxRows,
	onBlur,
	onCancel,
	onChange,
	onFocus,
	onSave,
	onStartEditing,
	placeholder,
	processingResult,
	rules,
	saveOnBlur,
	saveOnEnter,
	saveOnTab,
	showProcessing,
	skipSpellcheck,
	tooltip,
	value
}, ref) => {
	const { isReadOnly: isUserReadOnly } = useCurrentUser();

	const [ state, setState ] = useState<PlaintextEditorState>({
		editingValue: value ?? '',
		isEditing: false,
		isProcessing: false,
		showValidation: false,
		validationMessage: ''
	});

	const valueBackupRef = useRef<string>(value ?? '');
	const textRef = useRef<TextAreaRef>(null);

	const isEditable = !isReadOnly && !isDisabled && !isUserReadOnly;

	const handleSave = useCallback(async(e?: React.MouseEvent) => {
		e?.preventDefault();
		e?.stopPropagation();

		if (state.isEditing) {
			setState((current) => ({
				...current,
				isEditing: false,
				isProcessing: true,
				showValidation: false
			}));

			const result = onSave(state.editingValue);

			if (result?.then) {
				await result;
			}

			setState((current) => ({
				...current,
				isEditing: false,
				isProcessing: false
			}));

			textRef.current?.blur();

			if (onBlur) {
				onBlur();
			}
		}
	}, [ onBlur, onSave, state.editingValue, state.isEditing ]);

	useEffect(() => {
		if (
			value !== state.editingValue &&
			!state.isEditing &&
			!state.isProcessing &&
			!state.showValidation &&
			(!processingResult || processingResult.success)
		) {
			setState((current) => ({
				...current,
				editingValue: value ?? ''
			}));
		}
	}, [ processingResult, state.editingValue, state.isEditing, state.isProcessing, state.showValidation, value ]);

	const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const currentValue = e.target.value;

		const message = validateText(currentValue, rules);

		setState((current) => ({
			...current,
			editingValue: currentValue,
			showValidation: Boolean(message),
			validationMessage: message ?? current.validationMessage
		}));

		if (onChange) {
			onChange(currentValue);
		}
	};

	const handleCancel = useCallback((e?: React.MouseEvent) => {
		e?.preventDefault();
		e?.stopPropagation();

		let targetValue = valueBackupRef.current;

		if (state.showValidation) {
			targetValue = state.editingValue;
		}

		setState((current) => ({
			...current,
			editingValue: targetValue,
			isEditing: false,
			showValidation: current.showValidation
		}));

		textRef.current?.blur();

		if (onCancel) {
			onCancel();
		}
	}, [ onCancel, state.editingValue, state.showValidation ]);

	const handleClick = useCallback((e?: MouseEvent | React.FocusEvent<HTMLTextAreaElement>) => {
		e?.preventDefault();
		e?.stopPropagation();

		if (onFocus) {
			onFocus();
		}

		if (!state.isEditing) {
			valueBackupRef.current = state.editingValue;

			setState((current) => ({
				...current,
				isEditing: true,
				isProcessing: false
			}));

			if (onStartEditing) {
				onStartEditing();
			}
		}

		setTimeout(() => textRef.current?.focus(), 50);
	}, [ onFocus, onStartEditing, state.editingValue, state.isEditing ]);

	const handleClickOutside = useCallback(() => {
		if (state.isEditing) {
			if (saveOnBlur && !state.showValidation) {
				handleSave();
			} else {
				handleCancel();
			}
		}
	}, [ handleCancel, handleSave, saveOnBlur, state.isEditing, state.showValidation ]);

	const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.code === 'Escape') {
			handleCancel();
		}
	};

	const handleEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (saveOnEnter && e.code === 'Enter' && !e.shiftKey && !state.showValidation) {
			handleSave();
		}

		if (!allowNewline) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.code === 'Tab') {
			if (saveOnTab) {
				if (!state.showValidation) {
					setTimeout(() => handleSave(), 25);
				}
			} else {
				e.preventDefault();
				e.stopPropagation();
			}
		}
	};

	useImperativeHandle(ref, () => ({
		edit: handleClick,
		save: () => onSave(value ?? '')
	}));

	const validationProcessingResult = {
		message: state.isEditing ? undefined : state.validationMessage,
		success: false
	};

	const processingState = {
		isProcessing: isProcessing ?? false,
		result: state.showValidation ? validationProcessingResult : processingResult
	};

	const isSingleLine = !allowNewline ||
		(!hasNewline(state.editingValue) && textRef.current?.resizableTextArea?.textArea.rows === 1);

	return (
		<Tooltip
			placement='bottom'
			title={ tooltip }
			trigger='hover'>
			<Tooltip
				open={ state.isEditing && state.showValidation }
				placement='bottom'
				title={ state.validationMessage }>
				<OutsideClickWrapper
					className={ classNames(
						'plaintext-editor',
						{
							'editing': state.isEditing,
							'periscope': isPeriscope,
							'readonly': isReadOnly || isUserReadOnly,
							'single-line': isSingleLine
						},
						className
					) }
					id={ id }
					onClick={ isEditable ? handleClick : haltClickEventPropagation }
					onClickOutside={ isEditable ? handleClickOutside : undefined }>
					<EditBox
						editIcon={ !isPeriscope }
						isEmpty={ isPeriscope && !state.isEditing && !value }
						isReadOnly={ isReadOnly || isUserReadOnly }
						isSalesforce={ isSalesforce }
						isSpreadsheet={ isSpreadsheet }
						state={ showProcessing || state.showValidation ? processingState : undefined }>
						<div className='plaintext-editor-wrapper'>
							<Input.TextArea
								autoSize={ {
									maxRows,
									minRows: 1
								} }
								disabled={ !isEditable }
								maxLength={ maxLength }
								onChange={ handleOnChange }
								onFocus={ handleClick }
								onKeyDown={ handleKeyDown }
								onKeyUp={ handleKeyUp }
								onPressEnter={ handleEnter }
								placeholder={ placeholder }
								ref={ textRef }
								spellCheck={ !skipSpellcheck }
								value={ state.editingValue }
								variant='borderless' />
						</div>
						{ state.isEditing && !hideButtons &&
							<div>
								<Button
									className='action'
									disabled={ !canBeEmpty && !state.editingValue }
									onClick={ handleSave }
									type='primary'>
									{ COPY_GENERIC.SAVE }
								</Button>
								<Button
									className='action'
									onClick={ handleCancel }>
									{ COPY_GENERIC.CANCEL }
								</Button>
							</div> }
					</EditBox>
				</OutsideClickWrapper>
			</Tooltip>
		</Tooltip>
	);
});
